import React, { useState, useEffect, useRef } from 'react';
import { getImagesData } from '../services/api';
import './ImageDropdown.css';
import { matchesSearch } from '../utils/search';

const ImageDropdown = ({ value, onChange, language = 'english', placeholder = "Select image..." }) => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch images on component mount
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const response = await getImagesData();
        const imagesData = response.data?.[language] || {};
        
        // Convert object to array of {key, value} pairs
        const imagesArray = Object.entries(imagesData).map(([key, value]) => ({
          key,
          value
        }));
        
        setImages(imagesArray);
        setFilteredImages(imagesArray);
      } catch (error) {
        console.error('Error fetching images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [language]);

  // Filter images based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredImages(images);
    } else {
      const filtered = images.filter(image => 
        matchesSearch(searchTerm, image.key, image.value)
      );
      setFilteredImages(filtered);
    }
  }, [searchTerm, images]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (image) => {
    onChange(image.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value) return '';
    const selectedImage = images.find(img => img.value === value);
    return selectedImage ? `${selectedImage.key} (${selectedImage.value})` : value;
  };

  return (
    <div className="image-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="image-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="image-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading images...</div>
          ) : filteredImages.length === 0 ? (
            <div className="dropdown-no-results">No images found</div>
          ) : (
            filteredImages.map((image) => (
              <div
                key={image.key}
                className="dropdown-option"
                onClick={() => handleSelect(image)}
              >
                <div className="image-key">{image.key}</div>
                <div className="image-value">{image.value}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ImageDropdown;
